import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Eye, Lock, MousePointerClick, Users } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORE = "medlyn-insights-key";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7">
    <div className="w-11 h-11 rounded-xl bg-brand-navy dark:bg-brand-sky flex items-center justify-center">
      <Icon size={18} className="text-white dark:text-brand-ink" />
    </div>
    <p className="mt-4 font-display text-3xl font-bold text-slate-900 dark:text-white" data-testid={testid}>{value}</p>
    <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const Bars = ({ title, rows, keyName, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7" data-testid={testid}>
      <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-white mb-5">{title}</h2>
      {rows.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r[keyName]}>
            <div className="flex justify-between text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              <span className="truncate pr-4">{r[keyName] || "—"}</span>
              <span>{r.count}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200/70 dark:bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-brand-navy to-brand-sky" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Insights = () => {
  const [data, setData] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const load = async (key) => {
    try {
      const r = await axios.get(`${API}/api/analytics/summary`, { headers: { "X-Insights-Key": key } });
      sessionStorage.setItem(KEY_STORE, key);
      setData(r.data);
      setUnlocked(true);
      setError("");
    } catch (e) {
      sessionStorage.removeItem(KEY_STORE);
      setError(e.response?.status === 401 ? "Incorrect password." : "Could not load analytics.");
    }
  };

  useEffect(() => {
    document.title = "Visitor Insights — Medlyn 360";
    const stored = sessionStorage.getItem(KEY_STORE);
    if (stored) load(stored);
    return () => { document.title = "Medlyn 360 — Virtual Live Human Medical Scribing"; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="insights-page">
      <Navbar />
      <section className="bg-white dark:bg-brand-ink pt-36 pb-24 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-4">Internal</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Visitor Insights</h1>
          <p className="mt-4 text-sm text-slate-400">Consent-based, first-party analytics · last 30 days</p>

          {!unlocked ? (
            <form
              onSubmit={(e) => { e.preventDefault(); load(password); }}
              className="mt-12 max-w-sm rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8"
              data-testid="insights-password-form"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-navy dark:bg-brand-sky flex items-center justify-center mb-5">
                <Lock size={17} className="text-white dark:text-brand-ink" />
              </div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">This dashboard is password protected.</p>
              <input
                data-testid="insights-password-input"
                type="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-4 w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
              />
              {error && <p className="mt-2 text-xs font-semibold text-red-500" data-testid="insights-password-error">{error}</p>}
              <button
                type="submit"
                data-testid="insights-unlock-button"
                className="mt-4 w-full rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-6 py-3 text-sm hover:shadow-[0_12px_30px_-10px_rgba(42,49,146,0.6)] transition-shadow duration-300"
              >
                Unlock
              </button>
            </form>
          ) : !data ? (
            <p className="mt-12 text-sm text-slate-400">Loading…</p>
          ) : (
            <>
              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                <Stat icon={Eye} label="Pageviews (30 days)" value={data.total_pageviews} testid="insights-pageviews" />
                <Stat icon={Users} label="Unique sessions" value={data.unique_sessions} testid="insights-sessions" />
              </div>
              <div className="mt-6 grid lg:grid-cols-2 gap-5">
                <Bars title="Pageviews by page" rows={data.pageviews_by_path} keyName="path" testid="insights-pages" />
                <Bars title="Key actions" rows={data.clicks_by_name} keyName="name" testid="insights-clicks" />
              </div>
              <p className="mt-8 flex items-center gap-2 text-xs text-slate-400">
                <BarChart3 size={13} /> Only visitors who accept the consent banner are counted. No third-party trackers.
                <MousePointerClick size={13} className="ml-2" /> Actions tracked: consultation CTAs, service exploration, form submissions.
              </p>
            </>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
};
export default Insights;
